import React from 'react'
import { Card, CaptionText, BodyText, Button, Link } from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import * as styles from './activity.css'
import { SeeMoreButton } from '../../../components/SeeMoreButton'
import { HOME_PATH, GROUP_STATUS } from '../../../../../constants'
import { ActivityItems } from './ActivityItems'
import type { ActivityInformation, Edge } from '../../../../../../../typings'
import {
  getGroupDetailPageVariant,
  tracking,
} from '../../../../../lib/analytics/fpti'
import Events from '../../../../../lib/analytics/eventTags.js'
import serverData from '../../../../../utils/server-data'

interface ActivityProps {
  groupId: string
  isPayPalUser?: boolean
  activities: Edge<ActivityInformation>[]
  totalCountActivities: number
  groupStatus?: string
  onContribute?: () => void
  isAdmin?: boolean
}

const Activity = (props: ActivityProps) => {
  const {
    isPayPalUser,
    activities,
    totalCountActivities,
    groupId,
    groupStatus,
    isAdmin,
  } = props

  const onLogin = () => {
    tracking(
      Events.p2p_pools_detail_login_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: 'guest_contributor',
      }),
    )
  }

  const onSignup = () => {
    tracking(
      Events.p2p_pools_detail_sign_up_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: 'guest_contributor',
      }),
    )
  }

  const onActivityLinkClick = () => {
    tracking(
      Events.p2p_pools_detail_recent_activity_transaction_pressed({
        source: serverData.trackingParameters.externalSource,
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
      }),
    )
  }

  const loginSection = (
    <div className={styles.loginSectionContainer} data-testid="activityLogin">
      <div className={styles.loginToViewDetailsContainer}>
        <CaptionText as="p" className={styles.loginToViewDetails}>
          <Message id="activity.loginActivityDetails" />
        </CaptionText>
      </div>
      <Button
        size="sm"
        as="a"
        secondary
        href={`/signin/?returnUri=${HOME_PATH}/${groupId}`}
        data-testid="loginButton"
        onClick={onLogin}
      >
        <Message id="activity.login" />
      </Button>
      <div className={styles.signUpContainer}>
        <CaptionText as="p" className={styles.havePaypalAccount}>
          <Message id="activity.havePaypalAccount" />
        </CaptionText>
        <Link
          href="/"
          size="sm"
          onClick={onSignup}
          data-testid="signupLinkButton"
        >
          <Message id="activity.signup" />
        </Link>
      </div>
    </div>
  )

  const renderTransactionsList = () => {
    return (
      <>
        <ul className={styles.noBulletStyles} data-testid="activityList">
          <ActivityItems
            isAdmin={isAdmin}
            activities={activities}
            onActivityLinkClick={onActivityLinkClick}
          />
        </ul>
        {activities?.length < totalCountActivities && (
          <SeeMoreButton
            groupId={groupId}
            activities={activities}
            isAdmin={isAdmin}
            isPayPalUser={isPayPalUser}
          />
        )}
      </>
    )
  }

  const renderEmptyActivities = () => {
    if (groupStatus !== GROUP_STATUS.ACTIVE) {
      return null
    }

    return (
      <Card data-testid="emptyActivities" className={styles.activityContainer}>
        <BodyText>
          <Message id="activity.beTheFirst" />
        </BodyText>
        <Button
          size="sm"
          secondary
          onClick={props.onContribute}
          data-testid="contributeButton"
        >
          <Message id="flatProgress.contribute" />
        </Button>
      </Card>
    )
  }

  if (isPayPalUser) {
    return activities?.length > 0
      ? renderTransactionsList()
      : renderEmptyActivities()
  }

  return (
    <Card data-testid="loginDetail" className={styles.activityContainer}>
      {loginSection}
    </Card>
  )
}

export default Activity
